import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShoppingBag, 
  Package, 
  Store, 
  TrendingUp, 
  Users,
  Plus,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  Activity,
  BarChart3,
  FileText,
  Calendar,
  Globe,
  Edit,
  Trash2,
  X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import ApiService from '../services/api';
import DetectionAPI from '../services/detectionApi';
import SystemHealth from '../components/SystemHealth';
import AdvancedAnalytics from '../components/AdvancedAnalytics';
import SmartAlerts from '../components/SmartAlerts';
import ActivityFeed from '../components/ActivityFeed';
import { useProcessingJobMonitor } from '../hooks/useProcessingJobMonitor';
import { useDashboardData } from '../hooks/useDashboardData';

const Dashboard: React.FC = () => {
  const [apiHealth, setApiHealth] = React.useState<any>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [detectionStats, setDetectionStats] = React.useState<any>(null);
  const [realStats, setRealStats] = React.useState<any>(null);
  const [recentOffers, setRecentOffers] = React.useState<any[]>([]);
  const [recentProducts, setRecentProducts] = React.useState<any[]>([]);
  const [merchants, setMerchants] = React.useState<any[]>([]);
  const [editingOffer, setEditingOffer] = React.useState<any>(null);
  const [deletingOfferId, setDeletingOfferId] = React.useState<number | null>(null);
  const [cities, setCities] = React.useState<any[]>([]);
  const [useGraphQL, setUseGraphQL] = React.useState(true);
  
  // Initialize processing job monitor
  const { monitorOfferProcessing, monitorDetection, monitorAnalysis, getActiveJobs } = useProcessingJobMonitor();
  
  // GraphQL dashboard data hook (new)
  const { 
    data: graphqlDashboardData, 
    loading: graphqlLoading, 
    error: graphqlError, 
    refetch: refetchGraphQL, 
    isUsingGraphQL 
  } = useDashboardData(useGraphQL);

  React.useEffect(() => {
    // Use GraphQL data when available, otherwise fall back to REST
    if (graphqlDashboardData && useGraphQL) {
      console.log('📊 Using GraphQL data:', graphqlDashboardData);
      setRealStats({
        totalOffers: graphqlDashboardData.stats.totalOffers,
        activeOffers: graphqlDashboardData.stats.activeOffers,
        processingOffers: graphqlDashboardData.stats.processingOffers,
        totalProducts: graphqlDashboardData.stats.totalProducts,
        totalMerchants: graphqlDashboardData.stats.totalMerchants,
        avgConfidence: graphqlDashboardData.stats.avgConfidence / 100, // Convert back to 0-1 scale
        draftOffers: graphqlDashboardData.stats.totalOffers - graphqlDashboardData.stats.activeOffers - graphqlDashboardData.stats.processingOffers,
        expiredOffers: 0,
        totalImages: 0,
        recentActivity: graphqlDashboardData.stats.totalOffers + graphqlDashboardData.stats.totalProducts,
      });
      setRecentOffers(graphqlDashboardData.recentOffers);
      setRecentProducts(graphqlDashboardData.recentProducts);
      setMerchants(graphqlDashboardData.merchants);
      setCities(graphqlDashboardData.cities);
      setIsLoading(false);
      return;
    }
  }, [graphqlDashboardData, useGraphQL]);

  React.useEffect(() => {
    // Only load REST data if not using GraphQL or GraphQL failed
    if (!useGraphQL || (useGraphQL && graphqlError && !graphqlDashboardData)) {
      loadDashboardDataRest();
    }
  }, [useGraphQL, graphqlError]);

  const loadDashboardDataRest = async () => {
    try {
      console.log('🔄 تحميل بيانات لوحة التحكم (REST)...');
      setIsLoading(true);
        
      // تحميل جميع البيانات بالتوازي
      const [
        healthData,
        offersData,
        productsData,
        merchantsData,
        detectionStatsData,
        citiesData
      ] = await Promise.all([
        ApiService.getDetailedHealth().catch(e => ({ status: 'error', message: 'فشل في الاتصال' })),
        ApiService.getAllOffers({ limit: 5 }).catch(e => []),
        ApiService.getProducts({ limit: 10 }).catch(e => []),
        ApiService.getMerchants({ limit: 5 }).catch(e => []),
        DetectionAPI.getDetectionStats().catch(e => null),
        ApiService.getCities().catch(e => [])
      ]);

        console.log('✅ تم تحميل البيانات:', {
          health: healthData,
          offers: offersData?.length || 0,
          products: productsData?.length || 0,
          merchants: merchantsData?.length || 0
        });

        setApiHealth(healthData);
        setRecentOffers(Array.isArray(offersData) ? offersData : []);
        setRecentProducts(Array.isArray(productsData) ? productsData : []);
        setMerchants(Array.isArray(merchantsData) ? merchantsData : []);
        setCities(Array.isArray(citiesData) ? citiesData : []);
        setDetectionStats(detectionStatsData);
        
        // حساب الإحصائيات الحقيقية من البيانات المحملة
        const offers = Array.isArray(offersData) ? offersData : [];
        const products = Array.isArray(productsData) ? productsData : [];
        const merchantsList = Array.isArray(merchantsData) ? merchantsData : [];
        
        // حساب إحصائيات أكثر تفصيلاً
        const activeOffers = offers.filter(o => o.status === 'active').length;
        const processingOffers = offers.filter(o => o.status === 'processing').length;
        const draftOffers = offers.filter(o => o.status === 'draft').length;
        const expiredOffers = offers.filter(o => o.status === 'expired').length;
        
        // حساب متوسط الثقة من المنتجات
        const confidenceScores = products.filter(p => p.confidence_score || p.confidence).map(p => p.confidence_score || p.confidence);
        const avgConfidence = confidenceScores.length > 0 
          ? confidenceScores.reduce((sum, c) => sum + c, 0) / confidenceScores.length 
          : 0.94; // قيمة افتراضية
        
        setRealStats({
          totalOffers: offers.length,
          activeOffers,
          totalProducts: products.length,
          totalMerchants: merchantsList.length,
          processingOffers,
          draftOffers,
          expiredOffers,
          avgConfidence,
          totalImages: offers.reduce((sum, o) => sum + (o.images_count || 0), 0),
          recentActivity: offers.length + products.length
        });
        
    } catch (error) {
      console.error('❌ خطأ في تحميل بيانات لوحة التحكم:', error);
      setApiHealth({
        status: 'error',
        message: 'فشل في تحميل البيانات',
        error: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    // Load dashboard data on mount and set interval for updates
    if (!useGraphQL || (useGraphQL && graphqlError && !graphqlDashboardData)) {
      loadDashboardDataRest();
      
      // تحديث البيانات كل 30 ثانية
      const interval = setInterval(loadDashboardDataRest, 30000);
      return () => clearInterval(interval);
    }
  }, [useGraphQL, graphqlError, graphqlDashboardData]);

  // Note: Write operations (create, update, delete) always use REST API
  // GraphQL is used for read operations only when enabled
  const handleEditOffer = async (updatedData: any) => {
    try {
      await ApiService.updateOffer(editingOffer.id, updatedData);
      setEditingOffer(null);
      // Reload data to show updated offer
      window.location.reload();
    } catch (error) {
      console.error('Error updating offer:', error);
      alert('فشل في تحديث العرض');
    }
  };

  const handleDeleteOffer = async (offerId: number) => {
    try {
      await ApiService.deleteOffer(offerId);
      setDeletingOfferId(null);
      // Remove the deleted offer from the list
      setRecentOffers(recentOffers.filter(offer => offer.id !== offerId));
      // Update stats
      setRealStats(prev => prev ? {
        ...prev,
        totalOffers: prev.totalOffers - 1,
        activeOffers: prev.activeOffers - (recentOffers.find(o => o.id === offerId)?.status === 'active' ? 1 : 0)
      } : null);
    } catch (error) {
      console.error('Error deleting offer:', error);
      alert('فشل في حذف العرض');
    }
  };

  // Demo function to simulate processing job
  const simulateProcessingJob = () => {
    const jobId = `job_${Date.now()}`;
    const offerTitle = 'عرض تجريبي - كارفور الأسبوعي';
    
    // Start monitoring the job
    monitorOfferProcessing(jobId, '999', offerTitle);
    
    // Simulate job completion after 8 seconds
    setTimeout(() => {
      // In a real implementation, the monitoring hook would detect completion automatically
      console.log('تم إنتهاء المعالجة التجريبية');
    }, 8000);
  };

  const stats = [
    {
      title: 'إجمالي العروض',
      value: realStats?.totalOffers || 0,
      change: realStats?.totalOffers > 0 ? `+${realStats.totalOffers}` : '0',
      changeType: realStats?.totalOffers > 0 ? 'positive' : 'neutral',
      icon: ShoppingBag,
      color: 'primary',
      description: 'جميع العروض المسجلة'
    },
    {
      title: 'العروض النشطة',
      value: realStats?.activeOffers || 0,
      change: realStats?.activeOffers > 0 ? `${Math.round((realStats.activeOffers / Math.max(realStats.totalOffers, 1)) * 100)}%` : '0%',
      changeType: realStats?.activeOffers > 0 ? 'positive' : 'neutral',
      icon: TrendingUp,
      color: 'success',
      description: 'العروض المنشورة حالياً'
    },
    {
      title: 'المنتجات المستخرجة',
      value: realStats?.totalProducts || 0,
      change: realStats?.totalProducts > 0 ? `+${realStats.totalProducts}` : '0',
      changeType: realStats?.totalProducts > 0 ? 'positive' : 'neutral',
      icon: Package,
      color: 'secondary',
      description: 'المنتجات المكتشفة والمحللة'
    },
    {
      title: 'المتاجر المسجلة',
      value: realStats?.totalMerchants || 0,
      change: realStats?.totalMerchants > 0 ? `+${realStats.totalMerchants}` : '0',
      changeType: realStats?.totalMerchants > 0 ? 'positive' : 'neutral',
      icon: Store,
      color: 'purple',
      description: 'المتاجر والهايبرماركت'
    },
    {
      title: 'قيد المعالجة',
      value: realStats?.processingOffers || 0,
      change: realStats?.processingOffers > 0 ? `${realStats.processingOffers} جارٍ` : 'لا يوجد',
      changeType: realStats?.processingOffers > 0 ? 'warning' : 'neutral',
      icon: Clock,
      color: 'warning',
      description: 'العروض تحت المعالجة'
    },
    {
      title: 'دقة الاستخراج',
      value: `${Math.round(realStats?.avgConfidence * 100 || 94)}%`,
      change: realStats?.avgConfidence > 0.9 ? 'ممتاز' : realStats?.avgConfidence > 0.8 ? 'جيد' : 'متوسط',
      changeType: realStats?.avgConfidence > 0.9 ? 'positive' : realStats?.avgConfidence > 0.8 ? 'neutral' : 'negative',
      icon: CheckCircle,
      color: 'indigo',
      description: 'متوسط دقة الذكاء الاصطناعي'
    },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div>
          <h1 className="text-responsive-xl font-bold gradient-text">
            مرحباً بك في نظام معالجة منشورات العروض
          </h1>
          <p className="text-gray-600 mt-2">
            تحليل ذكي للعروض التجارية باستخدام الذكاء الاصطناعي
          </p>
          <div className="mt-3 flex items-center gap-3">
            <span className="text-sm text-gray-600">API:</span>
            <button
              onClick={() => setUseGraphQL(!useGraphQL)}
              className={`text-xs px-3 py-1 rounded-lg transition-colors ${
                isUsingGraphQL 
                  ? 'bg-success-100 text-success-700' 
                  : 'bg-warning-100 text-warning-700'
              }`}
            >
              {isUsingGraphQL ? '📊 GraphQL' : '🔄 REST'}
            </button>
            {graphqlError && (
              <span className="text-xs text-error-600">
                GraphQL فشل - تبديل إلى REST
              </span>
            )}
            {graphqlLoading && (
              <span className="text-xs text-primary-600">
                🔄 تحميل...
              </span>
            )}
          </div>
        </div>
        <Link
          to="/offers/new"
          className="btn-primary group"
        >
          <Plus className="w-5 h-5 ml-2 group-hover:rotate-90 transition-transform duration-300" />
          إنشاء عرض جديد
        </Link>
      </div>

      {/* API Status */}
      {apiHealth && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`card p-4 ${
            apiHealth.status === 'healthy' || apiHealth.status === 'ok'
              ? 'border-success-200 bg-success-50/30' 
              : 'border-error-200 bg-error-50/30'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${
              apiHealth.status === 'healthy' || apiHealth.status === 'ok'
                ? 'bg-success-500 animate-pulse' 
                : 'bg-error-500'
            }`}></div>
            <span className="font-medium">
              {apiHealth.status === 'healthy' || apiHealth.status === 'ok'
                ? '✅ النظام يعمل بشكل سليم' 
                : '❌ هناك مشكلة في الاتصال'}
            </span>
            {apiHealth.message && (
              <span className="text-sm text-gray-600">- {apiHealth.message}</span>
            )}
          </div>
        </motion.div>
      )}

      {/* Stats Grid */}
      <div className="metrics-grid">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className={`icon-${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
                <div className={`flex items-center space-x-1 space-x-reverse text-sm font-medium ${
                  stat.changeType === 'positive' ? 'text-success-600' : 
                  stat.changeType === 'negative' ? 'text-error-600' : 'text-gray-500'
                }`}>
                  {stat.changeType === 'positive' && <ArrowUp className="w-4 h-4" />}
                  {stat.changeType === 'negative' && <ArrowDown className="w-4 h-4" />}
                  <span>{stat.change}</span>
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {stat.value}
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">{stat.title}</p>
                <p className="text-xs text-gray-600">{stat.description}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Offers */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="card-gradient"
        >
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-600" />
                العروض الأخيرة
              </h3>
              <Link to="/offers" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                عرض الكل
              </Link>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {recentOffers.length > 0 ? (
                recentOffers.map((offer) => (
                  <div key={offer.id} className="flex items-center space-x-4 space-x-reverse p-4 bg-white/60 rounded-xl hover:bg-white/80 transition-all duration-300 hover-lift group">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{offer.title}</h4>
                      <p className="text-sm text-gray-600">{offer.merchant_name}</p>
                      <div className="flex items-center space-x-4 space-x-reverse mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Package className="w-4 h-4" />
                          {offer.products_count} منتج
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {offer.views_count || 0} مشاهدة
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-left">
                        <span className={`badge ${
                          offer.status === 'active' ? 'badge-success' :
                          offer.status === 'processing' ? 'badge-warning' :
                          offer.status === 'expired' ? 'badge-error' :
                          'badge-primary'
                        }`}>
                          {offer.status === 'active' ? 'نشط' :
                           offer.status === 'processing' ? 'قيد المعالجة' :
                           offer.status === 'expired' ? 'منتهي' :
                           'مسودة'}
                        </span>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setEditingOffer(offer)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                          title="تعديل"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeletingOfferId(offer.id)}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>لا توجد عروض محفوظة بعد</p>
                  <Link to="/offers/new" className="btn-primary mt-4 text-sm">
                    إنشاء أول عرض
                  </Link>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Recent Products */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="card-gradient"
        >
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-secondary-600" />
                المنتجات المستخرجة حديثاً
              </h3>
              <Link to="/products" className="text-secondary-600 hover:text-secondary-700 text-sm font-medium">
                عرض الكل
              </Link>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {recentProducts.length > 0 ? (
                recentProducts.slice(0, 5).map((product) => (
                  <div key={product.id} className="flex items-center space-x-4 space-x-reverse p-4 bg-white/60 rounded-xl hover:bg-white/80 transition-all duration-300 hover-lift">
                    {product.image_url && (
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        <img 
                          src={product.image_url} 
                          alt={product.extracted_data?.product_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">
                        {product.extracted_data?.product_name || 'منتج غير محدد'}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        {product.extracted_data?.price_new && (
                          <span className="text-sm font-semibold text-success-600">
                            {product.extracted_data.price_new} ريال
                          </span>
                        )}
                        {product.extracted_data?.price_old && product.extracted_data?.price_old > product.extracted_data?.price_new && (
                          <span className="text-xs text-gray-500 line-through">
                            {product.extracted_data.price_old} ريال
                          </span>
                        )}
                        {product.extracted_data?.discount > 0 && (
                          <span className="badge badge-error text-xs">
                            -{Math.round(product.extracted_data.discount)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`w-2 h-2 rounded-full ${
                        product.confidence >= 0.8 ? 'bg-success-500' :
                        product.confidence >= 0.6 ? 'bg-warning-500' :
                        'bg-error-500'
                      }`}></div>
                      <span className="text-xs text-gray-500 block mt-1">
                        {Math.round(product.confidence * 100)}%
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>لا توجد منتجات مستخرجة بعد</p>
                  <Link to="/offers/new" className="btn-secondary mt-4 text-sm">
                    ابدأ المعالجة
                  </Link>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Activity Feed */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.35 }}
        >
          <ActivityFeed />
        </motion.div>
      </div>

      {/* System Health Monitoring */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <SystemHealth />
      </motion.div>

      {/* Smart Alerts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
      >
        <SmartAlerts />
      </motion.div>

      {/* Advanced Analytics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <AdvancedAnalytics />
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card-gradient"
      >
        <div className="card-header">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-accent-600" />
            إجراءات سريعة
          </h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/offers/new"
              className="flex items-center space-x-3 space-x-reverse p-4 bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl hover:from-primary-100 hover:to-primary-200 transition-all duration-300 hover-lift group"
            >
              <div className="icon-primary">
                <Plus className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-semibold text-primary-900">إنشاء عرض جديد</h4>
                <p className="text-sm text-primary-700">ابدأ معالجة فلاير جديد</p>
              </div>
            </Link>

            <Link
              to="/products"
              className="flex items-center space-x-3 space-x-reverse p-4 bg-gradient-to-r from-secondary-50 to-secondary-100 rounded-xl hover:from-secondary-100 hover:to-secondary-200 transition-all duration-300 hover-lift group"
            >
              <div className="icon-secondary">
                <Package className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-semibold text-secondary-900">مراجعة المنتجات</h4>
                <p className="text-sm text-secondary-700">راجع المنتجات المستخرجة</p>
              </div>
            </Link>

            <Link
              to="/merchants"
              className="flex items-center space-x-3 space-x-reverse p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl hover:from-purple-100 hover:to-purple-200 transition-all duration-300 hover-lift group"
            >
              <div className="icon-purple">
                <Store className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-semibold text-purple-900">إدارة المتاجر</h4>
                <p className="text-sm text-purple-700">أضف أو عدل المتاجر</p>
              </div>
            </Link>

            <Link
              to="/analytics"
              className="flex items-center space-x-3 space-x-reverse p-4 bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-xl hover:from-indigo-100 hover:to-indigo-200 transition-all duration-300 hover-lift group"
            >
              <div className="icon-indigo">
                <BarChart3 className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-semibold text-indigo-900">الإحصائيات</h4>
                <p className="text-sm text-indigo-700">عرض تقارير مفصلة</p>
              </div>
            </Link>
          </div>
          
          {/* Demo Processing Section */}
          <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-amber-600" />
                <h4 className="font-semibold text-amber-900">تجربة الإشعارات</h4>
              </div>
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">تجريبي</span>
            </div>
            <p className="text-sm text-amber-700 mb-4">
              اختبر نظام الإشعارات الذكية ومتابعة المعالجة في الوقت الفعلي
            </p>
            <div className="flex gap-2">
              <button
                onClick={simulateProcessingJob}
                className="flex-1 bg-amber-500 text-white px-3 py-2 rounded-lg hover:bg-amber-600 transition-colors text-sm flex items-center justify-center gap-2"
              >
                <Clock className="w-4 h-4" />
                محاكاة معالجة عرض
              </button>
              <button
                onClick={() => monitorDetection(`det_${Date.now()}`)}
                className="flex-1 bg-orange-500 text-white px-3 py-2 rounded-lg hover:bg-orange-600 transition-colors text-sm flex items-center justify-center gap-2"
              >
                <TrendingUp className="w-4 h-4" />
                محاكاة اكتشاف
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Edit Offer Modal */}
      {editingOffer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full mx-4"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">تعديل العرض</h3>
              <button
                onClick={() => setEditingOffer(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const updatedData = {
                  title: formData.get('title'),
                  description: formData.get('description'),
                  merchant_id: parseInt(formData.get('merchant_id') as string),
                  city_id: parseInt(formData.get('city_id') as string),
                  start_date: formData.get('start_date'),
                  end_date: formData.get('end_date'),
                };
                handleEditOffer(updatedData);
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  عنوان العرض
                </label>
                <input
                  type="text"
                  name="title"
                  defaultValue={editingOffer.title}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الوصف
                </label>
                <textarea
                  name="description"
                  defaultValue={editingOffer.description}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  المتجر
                </label>
                <select
                  name="merchant_id"
                  defaultValue={editingOffer.merchant_id}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  {merchants.map((merchant) => (
                    <option key={merchant.id} value={merchant.id}>
                      {merchant.name_arabic}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  المدينة
                </label>
                <select
                  name="city_id"
                  defaultValue={editingOffer.city_id}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name_arabic}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ البداية
                  </label>
                  <input
                    type="date"
                    name="start_date"
                    defaultValue={editingOffer.start_date?.split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ الانتهاء
                  </label>
                  <input
                    type="date"
                    name="end_date"
                    defaultValue={editingOffer.end_date?.split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                >
                  حفظ التغييرات
                </button>
                <button
                  type="button"
                  onClick={() => setEditingOffer(null)}
                  className="flex-1 btn-secondary"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deletingOfferId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-sm w-full mx-4"
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                حذف العرض
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                هل أنت متأكد من حذف هذا العرض؟ لا يمكن التراجع عن هذا الإجراء.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => handleDeleteOffer(deletingOfferId)}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  حذف
                </button>
                <button
                  onClick={() => setDeletingOfferId(null)}
                  className="flex-1 btn-secondary"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;